import { Errors } from '@cdn/contracts';

/**
 * A failed management request. `code` is the stable identifier of the contract vocabulary: it is compared and
 * shown raw, never translated. `message` is English technical text for troubleshooting; the localized
 * explanation comes from the catalogs.
 */
export class ApiProblem extends Error {
	#code: string;
	get code() {
		return this.#code;
	}

	#status: number;
	/** The HTTP status, 0 when no answer arrived */
	get status() {
		return this.#status;
	}

	#details: Record<string, unknown> | undefined;
	get details() {
		return this.#details;
	}

	#trace: { request?: string; time: string } = { time: new Date().toISOString() };
	/**
	 * What support needs to find this failure (decision D43): the identifier the request was sent under, which
	 * the service logs under the same name, and when it failed. Set by the client for every request it sends.
	 */
	get trace() {
		return this.#trace;
	}

	constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
		super(message);
		this.name = 'ApiProblem';
		this.#code = code;
		this.#status = status;
		this.#details = details;
	}

	/**
	 * Names the request this problem answers, and when it failed
	 */
	traced(request: string | undefined, time = new Date()): this {
		this.#trace = { ...(request ? { request } : {}), time: time.toISOString() };
		return this;
	}

	is(...codes: string[]) {
		return codes.includes(this.#code);
	}

	/** Whether the answer says the session the request was sent with ended (the family rule's 401 class) */
	get ended() {
		return this.is('UNAUTHENTICATED', 'SESSION_EXPIRED', 'SESSION_REVOKED', 'ACCOUNT_SUSPENDED');
	}

	/** Why the session ended, when the service said so: `expired`, `revoked` or `suspended` (D26) */
	get reason(): string | undefined {
		const given = this.#details?.reason;
		if (typeof given === 'string') return given;
		return { SESSION_EXPIRED: 'expired', SESSION_REVOKED: 'revoked', ACCOUNT_SUSPENDED: 'suspended' }[this.#code];
	}

	#quiet: { unsent?: string } | undefined;
	/**
	 * A session that ended, already answered by the page's `Session` (its silent renewal or its one dialog): no
	 * screen shows it as an error, and what was on screen stays
	 */
	get quiet() {
		return this.#quiet !== undefined;
	}

	/** Beside a destructive action that was not sent while the person was signed out: say so, instead of an error */
	get unsent() {
		return this.#quiet?.unsent;
	}

	/**
	 * Marks the problem as answered by the page's `Session`
	 *
	 * @param unsent The line to show beside the destructive action that was not sent
	 */
	hush(unsent?: string): this {
		this.#quiet = { unsent };
		return this;
	}

	/** The service or the authority behind it did not answer, as opposed to refusing the request */
	get outage() {
		return this.is('UNAVAILABLE', 'UPSTREAM_UNAVAILABLE') || this.#status >= 500;
	}

	/**
	 * Whether a change was sent and its answer never came, so it may or may not have been made. The screen says
	 * so and asks the person to check before trying again.
	 */
	get unknown() {
		return (this.#status === 0 || this.#relayed) && this.#details?.outcome === 'unknown';
	}

	// A change answered by a gateway or a platform in front of the service, never by the service itself
	#relayed = false;

	/**
	 * The service did not answer within the deadline of the request: an outage, never a refusal
	 *
	 * @param changing Whether the request asked for a change, whose outcome is then unknown
	 */
	static late(changing: boolean): ApiProblem {
		return new ApiProblem('UNAVAILABLE', 'The service did not answer in time', 0, changing ? { outcome: 'unknown' } : undefined);
	}

	/**
	 * Reads the error envelope of an answer without trusting its shape
	 *
	 * @param changing Whether the request asked for a change. Without the service's envelope the answer came from
	 * a gateway or a platform in front of it, which cannot say whether the service acted: a change is then an
	 * unknown outcome (decision D186), whatever the status, and a read of 502, 503 or 504 is an outage to wait out
	 */
	static from(status: number, body: unknown, changing = false): ApiProblem {
		const read = Errors.read(body);
		if (read) return new ApiProblem(read.code, read.message, status, read.details);
		if (status === 401) return new ApiProblem('UNAUTHENTICATED', `Unexpected answer with status ${status}`, status);
		if (changing) {
			const problem = new ApiProblem('UNAVAILABLE', `Unexpected answer with status ${status}`, status, { outcome: 'unknown' });
			problem.#relayed = true;
			return problem;
		}
		const code = [502, 503, 504].includes(status) ? 'UNAVAILABLE' : 'INTERNAL';
		return new ApiProblem(code, `Unexpected answer with status ${status}`, status);
	}

	/**
	 * Whatever was thrown, as a problem a screen can explain
	 */
	static of(thrown: unknown): ApiProblem {
		if (thrown instanceof ApiProblem) return thrown;
		return new ApiProblem('INTERNAL', thrown instanceof Error ? thrown.message : String(thrown), 0);
	}
}
